import React, { memo } from 'react'
import classNames from 'classnames'
import { ModalBase, MODAL_TYPES } from 'transfix-ui/components/Modals'
import { Grid, makeStyles } from 'transfix-ui/core'
import { IEquipmentTypes } from 'types/graphqlTypes'
import { useFeatureFlagContext } from 'utils/context/FeatureFlagsContext'
import { Tabs, Tab } from 'transfix-ui'
import { ShipperRateContext } from '../../context'
import { getAugmentedRate } from '../../utils/getAugmentedRate'
import { LaneHeader } from '../LaneHeader'
import DataTabContent from './DataTabContent'
import LaneDetailsTabContent from './LaneDetailsTabContent'
import NotesTabContent from './NotesTabContent'
import RatesTabContent from './RatesTabContent'
import Sidebar from './Sidebar'
import { AwardedLaneModalTab } from './constants'
import { useShipperRateQuery } from './graphql/ShipperRate'
import { useAwardedLaneModalTabs } from './hooks'

const useAwardedLaneModalStyles = makeStyles({
  modalBody: {
    display: 'flex',
    maxHeight: '100%',
  },
  gridContainer: {
    paddingTop: 5,
    maxHeight: '100%',
    margin: 0,
  },
  scrollSection: {
    display: 'block',
    overflow: 'auto',
    maxHeight: '100%',
  },
  sidebar: {
    margin: 0,
    overflowX: 'hidden',
  },
  tabList: {
    width: '100%',
    '& ~ [role="tabpanel"]': {
      width: '100%',
    },
  },
})

interface IAwardedLaneModalProps {
  awardedLaneId: string
  onClose: () => void
}

export const AwardedLaneModal = memo<IAwardedLaneModalProps>(({ awardedLaneId, onClose }) => {
  const classes = useAwardedLaneModalStyles()
  const { getFlag } = useFeatureFlagContext()

  const { enabled: isAwardedLaneListingDetailsEnabled } = getFlag('enableAwardedLaneListingDetails')
  const { enabled: isMpMarketInsightsEnabled } = getFlag('mpMarketInsights')

  const { data, loading } = useShipperRateQuery({
    variables: {
      id: awardedLaneId,
    },
  })
  const { shipperRate: awardedLane } = data || {}
  const { equipment } = awardedLane ?? {}
  const { parentKey } = equipment ?? {}

  const displayDataTab = awardedLane && parentKey === IEquipmentTypes.dry_van
  const dataTabTooltip = !displayDataTab ? 'Data only available for dry van lanes.' : undefined

  const selectedAwardedLane = awardedLane ? getAugmentedRate(awardedLane) : undefined

  const { activeTab, handleTabChange } = useAwardedLaneModalTabs()

  return (
    <ShipperRateContext.Provider
      value={{
        selectedShipperRate: selectedAwardedLane,
      }}>
      <ModalBase
        title={
          <LaneHeader
            dataTest='awarded-lane-modal-header'
            loading={loading}
            rate={selectedAwardedLane}
          />
        }
        size={10}
        open
        modalType={MODAL_TYPES.form}
        onClose={() => onClose()}
        dataTest='awarded-lanes-modal'
        headerClassName='box-shadow-low'
        bodyClassName={classes.modalBody}
        isClose>
        <Grid className={classes.gridContainer} container spacing={4}>
          <Grid className={classes.scrollSection} container item xs>
            <Tabs tabListClassName={classes.tabList} value={activeTab} onChange={handleTabChange}>
              <Tab disabled={!selectedAwardedLane} label={AwardedLaneModalTab.Rates}>
                {selectedAwardedLane ? (
                  <RatesTabContent currentRate={selectedAwardedLane} />
                ) : (
                  <></>
                )}
              </Tab>

              {isAwardedLaneListingDetailsEnabled && (
                <Tab disabled={!awardedLane} label={AwardedLaneModalTab.LaneDetails}>
                  {awardedLane ? <LaneDetailsTabContent awardedLane={awardedLane} /> : <></>}
                </Tab>
              )}

              {isMpMarketInsightsEnabled && (
                <Tab
                  disabled={!awardedLane || !displayDataTab}
                  tooltipMessage={dataTabTooltip}
                  label={AwardedLaneModalTab.Data}>
                  {awardedLane ? <DataTabContent awardedLane={awardedLane} /> : <></>}
                </Tab>
              )}

              <Tab disabled={!awardedLane} label={AwardedLaneModalTab.Notes}>
                <NotesTabContent noteableId={awardedLaneId} />
              </Tab>
            </Tabs>
          </Grid>

          <Grid
            className={classNames(classes.scrollSection, classes.sidebar)}
            container
            item
            xs={3}
            style={{
              paddingRight: 0,
            }}>
            <Sidebar awardedLane={awardedLane} loading={loading} />
          </Grid>
        </Grid>
      </ModalBase>
    </ShipperRateContext.Provider>
  )
})

export default AwardedLaneModal
